import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ROUTE_NAMES } from "../../routes/routeConstants";
import SheetGround from "../../components/shared/ui/SheetGround";
import { VELLUM as T, SANS, display, mono, body, btn } from "../../components/shared/folio/kit";
import { blogArticles, BlogArticle } from "./blogArticles";

/* Field notes, drawn as a folio.

   A blog index is a schedule of what has been written: the plates are ruled
   into columns rather than floated as cards, because nothing here sits on a
   different level from anything else. The one exception is the editor's
   pick, which is given the width to earn the name. */

const P = "bl";
const ALL = "All";

export const BlogPage: React.FC = () => {
  const categories = useMemo(() => {
    const set = new Set<string>([ALL]);
    blogArticles.forEach((a) => set.add(a.category));
    return Array.from(set);
  }, []);

  const [active, setActive] = useState<string>(ALL);
  const filtered = useMemo(
    () => (active === ALL ? blogArticles : blogArticles.filter((a) => a.category === active)),
    [active],
  );

  const featured: BlogArticle | undefined = filtered[0];
  const rest = filtered.slice(1);

  return (
    <SheetGround className={`${P}-root`} style={{ fontFamily: SANS, color: T.ink }}>
      {/* The sheet head */}
      <section style={{ maxWidth: 1280, margin: "0 auto", padding: "44px 20px 0" }}>
        <div
          style={{
            ...mono(T),
            borderBottom: `1px solid ${T.rule}`,
            paddingBottom: 12,
            marginBottom: 30,
            display: "flex",
            justifyContent: "space-between",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <span>§ Blog</span>
          <span>{blogArticles.length} essays</span>
          <span>Updated weekly</span>
        </div>

        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 24, flexWrap: "wrap" }}>
          <h1 style={{ ...display(T, "clamp(36px, 5.6vw, 66px)"), lineHeight: 0.98, letterSpacing: "-0.04em" }}>
            Field notes on{" "}
            <span style={{ color: T.accent, borderBottom: `3px solid ${T.accent}` }}>academic hiring</span>.
          </h1>
          <span style={mono(T, T.accent)}>§ Resources &amp; insights</span>
        </div>

        <p style={{ ...body(T), fontSize: 18, marginTop: 26, maxWidth: 640 }}>
          Expert insights, career tips, and industry trends for academic professionals and the institutions
          that hire them.
        </p>
      </section>

      {/* Filter, set on the rule that opens the schedule */}
      <section style={{ maxWidth: 1280, margin: "0 auto", padding: "34px 20px 0" }}>
        <div
          role="group"
          aria-label="Filter essays by category"
          className={`${P}-tabs`}
          style={{ display: "flex", flexWrap: "wrap", borderBottom: `1.5px solid ${T.ink}` }}
        >
          {categories.map((c, i) => {
            const on = active === c;
            return (
              <button
                key={c}
                onClick={() => setActive(c)}
                aria-pressed={on}
                className={`${P}-tab`}
                style={{
                  ...mono(T, on ? T.ink : T.mute),
                  position: "relative",
                  background: "transparent",
                  border: "none",
                  borderLeft: i === 0 ? "none" : `1px solid ${T.rule}`,
                  borderRadius: 0,
                  padding: "12px 18px",
                  cursor: "pointer",
                }}
              >
                {on && (
                  <span
                    aria-hidden
                    style={{ position: "absolute", left: 18, right: 18, bottom: -1.5, height: 2.5, background: T.accent }}
                  />
                )}
                {c}
              </button>
            );
          })}
        </div>
      </section>

      {/* Editor's pick */}
      {featured && (
        <section style={{ maxWidth: 1280, margin: "0 auto", padding: "0 20px" }}>
          <Link
            to={`${ROUTE_NAMES.PAGES.BLOG}/${featured.slug}`}
            className={`${P}-featured`}
            style={{
              display: "grid",
              gridTemplateColumns: "1.2fr 1fr",
              padding: "48px 0",
              gap: 48,
              alignItems: "center",
              color: T.ink,
              textDecoration: "none",
              borderBottom: `1.5px solid ${T.ink}`,
            }}
          >
            <div>
              <div style={{ ...mono(T, T.accent), marginBottom: 14, display: "flex", alignItems: "center", gap: 10 }}>
                <span aria-hidden style={{ width: 28, height: 1, background: T.accent }} />
                Editor's pick
              </div>
              <h2
                className={`${P}-featured-title`}
                style={{ ...display(T, "clamp(34px, 5.2vw, 60px)"), lineHeight: 1.02, letterSpacing: "-0.035em" }}
              >
                {featured.title}
              </h2>
              <p style={{ ...body(T), fontSize: 18, marginTop: 20, maxWidth: "60ch" }}>{featured.excerpt}</p>
              <div style={{ ...mono(T), marginTop: 22, display: "flex", gap: 14, flexWrap: "wrap" }}>
                <span>{featured.category}</span>
                <span aria-hidden>·</span>
                <span>{featured.readTime}</span>
                <span aria-hidden>·</span>
                <span>{featured.publishDate}</span>
              </div>
            </div>
            {/* The plate, framed as one. */}
            <div
              aria-hidden
              style={{
                aspectRatio: "4 / 3",
                background: T.panel,
                backgroundImage: `url(${featured.image})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                border: `1.5px solid ${T.ink}`,
              }}
            />
          </Link>
        </section>
      )}

      {/* The schedule */}
      <section style={{ maxWidth: 1280, margin: "0 auto", padding: "48px 20px 72px" }}>
        <div className={`${P}-grid`} style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)" }}>
          {rest.map((a, i) => (
            <Link
              key={a.slug}
              to={`${ROUTE_NAMES.PAGES.BLOG}/${a.slug}`}
              className={`${P}-card`}
              style={{
                padding: "26px 24px",
                borderTop: `1px solid ${T.rule}`,
                borderLeft: i % 3 !== 0 ? `1px solid ${T.rule}` : "none",
                color: T.ink,
                textDecoration: "none",
                display: "flex",
                flexDirection: "column",
                gap: 14,
              }}
            >
              <div
                aria-hidden
                style={{
                  aspectRatio: "16 / 10",
                  background: T.panel,
                  backgroundImage: `url(${a.image})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  border: `1px solid ${T.rule}`,
                }}
              />
              <div style={{ ...mono(T), display: "flex", gap: 12, flexWrap: "wrap" }}>
                <span>{a.category}</span>
                <span aria-hidden>·</span>
                <span>{a.readTime}</span>
              </div>
              <h3 className={`${P}-card-title`} style={{ ...display(T, "21px"), lineHeight: 1.18 }}>
                {a.title}
              </h3>
              <p style={{ ...body(T), fontSize: 14.5, lineHeight: 1.55 }}>{a.excerpt}</p>
              <span
                className={`${P}-read`}
                style={{
                  ...mono(T, T.ink),
                  marginTop: "auto",
                  display: "inline-flex",
                  gap: 8,
                  alignItems: "center",
                  alignSelf: "flex-start",
                  borderBottom: `1px solid ${T.ink}`,
                  paddingBottom: 4,
                }}
              >
                Read <span style={{ fontSize: 13 }}>→</span>
              </span>
            </Link>
          ))}
        </div>

        {/* Friday digest */}
        <div
          className={`${P}-digest`}
          style={{
            marginTop: 56,
            border: `1.5px solid ${T.ink}`,
            background: T.ground,
            padding: "38px 32px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 32,
            alignItems: "center",
          }}
        >
          <div>
            <div style={mono(T, T.accent)}>§ Friday digest</div>
            <h3 style={{ ...display(T, "clamp(24px, 3.2vw, 32px)"), margin: "10px 0 12px" }}>
              One email, Friday morning.
            </h3>
            <p style={{ ...body(T), fontSize: 16 }}>
              The week's new listings, the best essay, and one short note from the editor. No noise.
            </p>
          </div>
          <form style={{ display: "grid", gap: 12 }} onSubmit={(e) => e.preventDefault()}>
            <input
              aria-label="Email address for the Friday digest"
              placeholder="you@university.ac.uk"
              className={`${P}-input`}
              style={{
                background: "transparent",
                border: `1.5px solid ${T.ink}`,
                padding: "14px 16px",
                fontFamily: SANS,
                fontSize: 15,
                color: T.ink,
                outline: "none",
              }}
            />
            <button type="submit" className={`${P}-subscribe`} style={{ ...btn(T, "accent"), justifyContent: "center" }}>
              Subscribe
            </button>
          </form>
        </div>
      </section>

      <style>{`
        /* An entry answers by inking, never by lifting: the system has no
           elevation and these are ruled into a schedule, not stacked. */
        .${P}-card, .${P}-featured { transition: border-color 160ms ease; }
        .${P}-card-title, .${P}-featured-title, .${P}-tab { transition: color 140ms ease; }
        .${P}-card:hover { border-top-color: ${T.ink}; }
        .${P}-card:hover .${P}-card-title,
        .${P}-featured:hover .${P}-featured-title { color: ${T.accent}; }
        .${P}-read { transition: border-color 140ms ease, color 140ms ease; }
        .${P}-card:hover .${P}-read { color: ${T.accent}; border-bottom-color: ${T.accent}; }
        .${P}-tab:hover { color: ${T.ink}; }

        .${P}-input::placeholder { color: ${T.mute}; opacity: 1; }
        .${P}-input:focus { border-color: ${T.accent}; }
        .${P}-subscribe:hover { background: ${T.accentDeep}; }

        .${P}-card:focus-visible,
        .${P}-featured:focus-visible,
        .${P}-tab:focus-visible,
        .${P}-input:focus-visible,
        .${P}-subscribe:focus-visible {
          outline: 2px solid ${T.accent};
          outline-offset: 3px;
        }

        @media (prefers-reduced-motion: reduce) {
          .${P}-card, .${P}-featured, .${P}-card-title,
          .${P}-featured-title, .${P}-tab, .${P}-read { transition: none; }
        }

        @media (max-width: 1024px) {
          .${P}-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .${P}-card:nth-child(2n+1) { border-left: none !important; }
          .${P}-card:nth-child(2n) { border-left: 1px solid ${T.rule} !important; }
        }
        @media (max-width: 900px) {
          .${P}-featured { grid-template-columns: 1fr !important; gap: 32px !important; }
          .${P}-digest { grid-template-columns: 1fr !important; gap: 24px !important; }
        }
        @media (max-width: 640px) {
          .${P}-grid { grid-template-columns: 1fr !important; }
          .${P}-card { border-left: none !important; }
        }
      `}</style>
    </SheetGround>
  );
};
